"use client";
import AnimatedButtonContent from "@/components/animated-button-content";
import { DynamicDrawer } from "@/components/composite/family-drawer";
import { Form, FormField } from "@/components/kits/shadcn/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/kits/shadcn/input-otp";
import { cn, delayFor } from "@/lib/utils";
import { api } from "@/trpc/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { RectangleEllipsisIcon } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { }

export function Button({ children, className, ...props }: ButtonProps) {
  return (
    <button
      data-vaul-no-drag=""
      className={cn(
        "focus-visible:shadow-focus-ring-button flex h-12 w-full items-center gap-[15px] rounded-[16px] px-4 text-[17px] font-semibold transition-transform active:scale-95 max-sm:focus:scale-95 md:font-medium",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className, ...props }: ButtonProps) {
  return (
    <button
      data-vaul-no-drag=""
      className={cn(
        "focus-visible:shadow-focus-ring-button flex h-12 w-full items-center justify-center gap-[15px] rounded-full text-center text-[19px] font-semibold transition-transform focus:scale-95 active:scale-95 md:font-medium",
        // "bg-opacity-5 text-contrast/70 hover:bg-opacity-10",
        "bg-[#F7F8F9] text-[#222222]",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Header({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <header className="mt-[21px]">
      {icon}
      <h2 className="mt-2.5 font-open-runde text-[22px] font-semibold text-[#222222] md:font-medium">{title}</h2>
    </header>
  );
}

export function Description({ description, className }: { description: string; className?: string }) {
  return <p className={cn("mt-3 text-[17px] font-medium leading-[24px] text-[#999999] md:font-normal", className)}>{description}</p>;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> { }

export function Input({ className, ...props }: InputProps) {
  return (
    <input
      data-vaul-no-drag=""
      placeholder="Input"
      className={cn(
        "focus-visible:shadow-focus-ring-button flex h-12 w-full items-center gap-[15px] rounded-[16px] bg-[#F7F8F9] px-4 text-[17px] font-semibold text-[#222222] md:font-medium",
        className,
      )}
      {...props}
    />
  );
}

export function CodeInputView({ setView, close }: { setView: SetViewFn; close: () => void }) {
  const formSchema = z.object({
    code: z.string().min(1).max(5),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: "",
    },
  });

  const [mutationState, setMutationState] = React.useState<"init" | "pending" | "success" | "error">("init");

  async function mockClick() {
    setMutationState("pending");
    await delayFor(2000);
    if (Math.random() > 0.5) {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    } else {
      setMutationState("success");
      await delayFor(1000);
      setMutationState("init");
    }
  }

  const utils = api.useUtils();
  const mfaLoginMutation = api.auth.login.Mfa.finish.useMutation({
    onMutate: () => {
      setMutationState("pending");
    },
    onSuccess: (data) => {
      utils.auth.me.setData(void 0, () => data);
      close();
    },
    onError: async () => {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    if (mutationState !== "init") return;
    mfaLoginMutation.mutate(data);
  }

  return (
    <div>
      <div className="px-2">
        <Header icon={<RectangleEllipsisIcon className="size-12 opacity-40" />} title="Введите код" />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 overflow-clip">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <InputOTP {...field} maxLength={5} containerClassName="w-full justify-between group" pattern={REGEXP_ONLY_DIGITS_AND_CHARS}>
                  {Array.from({ length: 5 }, (_, i) => {
                    const isFirst = i === 0;
                    const isLast = i === 4;
                    return (
                      <InputOTPGroup key={i}>
                        <InputOTPSlot
                          index={i}
                          className={cn(
                            "size-12 rounded-lg bg-[#F7F8F9] font-mono font-medium uppercase",
                            isFirst && "rounded-l-[16px]",
                            isLast && "rounded-r-[16px]",
                          )}
                        />
                      </InputOTPGroup>
                    );
                  })}
                </InputOTP>
              )}
            />
            <Description description="Мы отправили одноразовый код на вашу почту, введите его для авторизации." />
            <Button
              type="submit"
              disabled={!form.formState.isValid}
              className={cn(
                "justify-center border",
                "disabled:pointer-events-none disabled:opacity-60",
                "transition-opacity duration-150 ease-in-out",
                mutationState === "pending" && "border-blue-900/20 bg-blue-100 text-blue-900",
                mutationState === "success" && "border-green-900/20 bg-green-100 text-green-900",
                mutationState === "error" && "border-red-900/20 bg-red-100 text-red-900",
              )}
            >
              {/* <LockIcon /> */}
              {/* Отправить код */}
              <AnimatedButtonContent
                {...{
                  isInit: mutationState === "init",
                  isError: mutationState === "error",
                  isPending: mutationState === "pending",
                  isSuccess: mutationState === "success",
                  copy: {
                    errorPrefix: "Не удалось",
                    action: {
                      isInit: "Проверить",
                      isPending: "Проверяем",
                      isError: "проверить",
                      isSuccess: "проверен",
                    },
                    subject: {
                      isInit: "код",
                      isPending: "код",
                      isError: "код",
                      isSuccess: "Код",
                    },
                  },
                }}
              />
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

export function EmailPasswordInputView({ setView, close }: { setView: SetViewFn; close: () => void }) {
  const formSchema = z.object({
    email: z.string().email().min(1),
    password: z.string().min(1, { message: "password is required" }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const utils = api.useUtils();

  const [mutationState, setMutationState] = React.useState<"init" | "pending" | "success" | "error">("init");

  async function mockClick() {
    setMutationState("pending");
    await delayFor(2000);
    if (Math.random() > 0.5) {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    } else {
      setMutationState("success");
      await delayFor(1000);
      close();
    }
  }

  const loginMutation = api.auth.login.password.useMutation({
    onMutate: () => {
      setMutationState("pending");
    },
    onSuccess: async (data) => {
      utils.auth.me.setData(void 0, () => data);
      setMutationState("success");
      await delayFor(1000);
      close();
    },
    onError: async () => {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    if (mutationState !== "init") return;
    loginMutation.mutate(data);
    // mockClick();
  }

  return (
    <>
      <header className="mb-4 flex h-[72px] items-center border-b border-[#F7F7F7] pl-2">
        <h2 className="mt-0 font-open-runde text-[19px] font-semibold text-[#222222] md:font-medium">Вход</h2>
      </header>
      <Form {...form}>
        <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField control={form.control} name="email" render={({ field }) => <Input placeholder="Email" {...field} />} />
          <FormField control={form.control} name="password" render={({ field }) => <Input type="password" placeholder="Пароль" {...field} />} />
          <Button
            type="submit"
            disabled={!form.formState.isValid}
            className={cn(
              "justify-center border",
              mutationState === "pending" && "border-blue-900/20 bg-blue-100 text-blue-900",
              mutationState === "success" && "border-green-900/20 bg-green-100 text-green-900",
              mutationState === "error" && "border-red-900/20 bg-red-100 text-red-900",
              "disabled:pointer-events-none disabled:opacity-60",
              "transition-opacity duration-150 ease-in-out",
            )}
          >
            <AnimatedButtonContent
              {...{
                isInit: mutationState === "init",
                isError: mutationState === "error",
                isPending: mutationState === "pending",
                isSuccess: mutationState === "success",
                copy: {
                  errorPrefix: "Неправильный",
                  action: {
                    isInit: "Войти",
                    isPending: "Проверяем пароль",
                    isError: "пароль",
                    isSuccess: "пожаловать",
                  },
                  subject: {
                    isInit: "",
                    isPending: "пароль",
                    isError: "",
                    isSuccess: "Добро",
                  },
                },
              }}
            />
          </Button>
          <SecondaryButton
            type="button"
            onClick={() => {
              console.log("updating view");
              console.log(typeof setView);
              setView("email-input");
            }}
          >
            Забыли пароль?
          </SecondaryButton>
        </form>
      </Form>
    </>
  );
}

export function EmailInputView({ setView }: { setView: SetViewFn }) {
  const formSchema = z.object({
    email: z.string().email().min(1),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const [mutationState, setMutationState] = React.useState<"init" | "pending" | "success" | "error">("init");

  async function mockClick() {
    setMutationState("pending");
    await delayFor(2000);
    if (Math.random() > 0.5) {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    } else {
      setView("code-input");
    }
  }

  const mfaLoginMutation = api.auth.login.Mfa.start.useMutation({
    onMutate: () => {
      setMutationState("pending");
    },
    onSuccess: (data) => {
      setView("code-input");
    },
    onError: async () => {
      setMutationState("error");
      await delayFor(2000);
      setMutationState("init");
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    if (mutationState !== "init") return;
    mfaLoginMutation.mutate(data);
  }

  return (
    <>
      <header className="mb-4 flex h-[72px] items-center border-b border-[#F7F7F7] pl-2">
        <h2 className="mt-0 font-open-runde text-[19px] font-semibold text-[#222222] md:font-medium">Вход по почте</h2>
      </header>
      <Form {...form}>
        <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField control={form.control} name="email" render={({ field }) => <Input placeholder="Email" {...field} />} />
          <Button
            type="submit"
            disabled={!form.formState.isValid}
            className={cn(
              "justify-center border",
              // mutationState === "pending" && "border-blue-900/20 bg-blue-100 text-blue-900",
              // mutationState === "success" && "border-green-900/20 bg-green-100 text-green-900",
              mutationState === "error" && "border-red-900/20 bg-red-100 text-red-900",
              "disabled:pointer-events-none disabled:opacity-60",
              "transition-opacity duration-150 ease-in-out",
            )}
          >
            {/* <LockIcon /> */}
            {/* Отправить код */}
            <AnimatedButtonContent
              {...{
                isInit: mutationState === "init",
                isError: mutationState === "error",
                isPending: mutationState === "pending",
                isSuccess: mutationState === "success",
                copy: {
                  errorPrefix: "Не удалось",
                  action: {
                    isInit: "Отправить",
                    isPending: "Отправляем",
                    isError: "отправить",
                    isSuccess: "отправлен",
                  },
                  subject: {
                    isInit: "код",
                    isPending: "код",
                    isError: "код",
                    isSuccess: "Код",
                  },
                },
              }}
            />
          </Button>
        </form>
      </Form>
    </>
  );
}

export type AuthState = "email-input" | "code-input" | "email-password-input";
export type SetViewFn = (view: AuthState) => void;

export function AuthDemo({ trigger }: { trigger: React.ReactNode }) {
  const [view, setView] = React.useState<"email-input" | "code-input" | "email-password-input">("email-password-input");
  const [open, setOpen] = React.useState<boolean>(false);

  const content = React.useMemo(() => {
    switch (view) {
      case "email-input":
        return <EmailInputView setView={setView} />;
      case "code-input":
        return <CodeInputView setView={setView} close={() => setOpen(false)} />;
      case "email-password-input":
        return <EmailPasswordInputView close={() => setOpen(false)} setView={setView} />;
    }
  }, [view]);

  React.useEffect(() => {
    setView("email-input");
  }, [open]);

  return (
    <>
      <div className="flex h-fit w-fit items-center justify-center" onClick={() => setOpen(true)}>
        {trigger}
      </div>
      <DynamicDrawer isOpen={open} setIsOpen={setOpen} content={content} contentKey={view} />
    </>
  );
}
